import { useTranslation } from 'react-i18next'
import { getFixedT } from '#app/middleware/i18next.ts'
import { type Route } from './+types/index.ts'
import { CodeSample } from './__code-sample.tsx'
import { CommandShowpiece } from './__command-palette.tsx'
import { FaqTeaser } from './__faq-teaser.tsx'
import { FeatureTeaser } from './__feature-teaser.tsx'
import { FinalCta } from './__final-cta.tsx'
import { Hero } from './__hero.tsx'
import { HowItWorks } from './__how-it-works.tsx'
import { MarketingLayout } from './__layout.tsx'
import { Playground } from './__playground.tsx'
import { Pricing } from './__pricing.tsx'
import { navSections } from './__sections.ts'
import { Work } from './__work.tsx'

// The whole anonymous landing renders localized copy from the `public`
// dictionary; declare it so the client loads the namespace (ADR-074).
export const handle = { i18n: ['public'] } as const

export function loader({ context }: Route.LoaderArgs) {
	// Localize the meta with the request's t-function; `meta` runs on the client
	// too, so it reads the resolved strings from loader data rather than the
	// server-only i18n instance.
	const t = getFixedT(context, 'public')
	return {
		meta: {
			title: t('meta.marketing.title'),
			description: t('meta.marketing.description'),
		},
	}
}

export const meta: Route.MetaFunction = ({ data }) => {
	const title = data?.meta.title ?? ''
	const description = data?.meta.description ?? ''
	return [
		{ title },
		{ name: 'description', content: description },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:type', content: 'website' },
	]
}

/**
 * Placeholder for a content section that ships in a later slice. Renders the
 * section landmark + heading so anchor links and scrollspy targets resolve and
 * the heading outline is correct; the real content replaces the body later.
 */
function SectionStub({ id, title }: { id: string; title: string }) {
	const { t } = useTranslation('public')
	return (
		<section
			id={id}
			aria-labelledby={`${id}-heading`}
			className="container scroll-mt-20 py-24"
		>
			<h2 id={`${id}-heading`} className="text-body-lg font-semibold">
				{title}
			</h2>
			<p className="text-muted-foreground mt-2">{t('landing.comingSoon')}</p>
		</section>
	)
}

/**
 * Real section components, keyed by their `navSections` id. Ids absent here fall
 * back to `SectionStub`, so each slice ships a section by adding one entry — no
 * edits to the render loop. Each component owns its own `id`/landmark markup.
 */
const sectionComponents: Partial<
	Record<(typeof navSections)[number]['id'], React.ComponentType>
> = {
	services: FeatureTeaser,
	pricing: Pricing,
	faq: FaqTeaser,
}

export default function Index() {
	// Section-stub headings reuse the shared navbar section labels in `common`.
	const { t: tc } = useTranslation('common')
	// Closing CTA copy comes from the home `public` dictionary; the shell itself
	// is parameterized so `/features` and `/faq` reuse it with their own copy.
	const { t } = useTranslation('public')
	return (
		<MarketingLayout>
			<Hero />
			{/*
			 * Showpiece + narrative sections are mounted explicitly here in their
			 * settled page order: the work proof, the process timeline, the ⌘K
			 * command palette, the live design system, then the code sample.
			 * `Work` is a `navSections` target but leads the page, so it's rendered
			 * here and skipped in the loop below (which handles the remaining nav
			 * sections in their declared order).
			 */}
			<Work />
			<HowItWorks />
			<CommandShowpiece />
			<Playground />
			<CodeSample />
			{navSections.map((section) => {
				if (section.id === 'work') return null
				const Section = sectionComponents[section.id]
				return Section ? (
					<Section key={section.id} />
				) : (
					<SectionStub
						key={section.id}
						id={section.id}
						title={tc(`nav.sections.${section.id}`)}
					/>
				)
			})}
			{/*
			 * The closing CTA is the `#contact` anchor the hero/header CTAs point
			 * to. It isn't a `navSections` target, so it's mounted explicitly after
			 * the nav sections, just before the footer.
			 */}
			<FinalCta
				heading={t('landing.finalCta.heading')}
				lead={t('landing.finalCta.lead')}
				primaryCta={{ label: t('landing.finalCta.ctaPrimary'), to: '/support' }}
				secondaryCta={{
					label: t('landing.finalCta.ctaSecondary'),
					to: '/support',
				}}
			/>
		</MarketingLayout>
	)
}
